'use client'
import { useEffect, useRef, useState } from 'react'

export type GlobalPin = {
  lat: number
  lng: number
  name: string
  slug: string
  collection: 'places' | 'services' | 'events'
  type: string
  typeLabel: string
  color: string
  cluster?: string
}

const GORIS_LAT = 39.5127
const GORIS_LNG = 46.3543

type Filter = 'all' | 'places' | 'services' | 'events'

export function GlobalMap({ pins, hideCollectionFilter, legend }: {
  pins: GlobalPin[]
  hideCollectionFilter?: boolean
  legend?: { label: string; color: string }[]
}) {
  const mapRef      = useRef<HTMLDivElement>(null)
  const mapInstance = useRef<any>(null)
  const allMarkers  = useRef<{ marker: any; pin: GlobalPin }[]>([])
  const [filter, setFilter] = useState<Filter>('all')
  const [counts, setCounts] = useState({ places: 0, services: 0, events: 0 })

  useEffect(() => {
    const el = mapRef.current
    if (!el || mapInstance.current || el.classList.contains('leaflet-container')) return

    import('leaflet').then(L => {
      if (!mapRef.current || mapRef.current.classList.contains('leaflet-container')) return

      delete (L.Icon.Default.prototype as any)._getIconUrl

      const map = L.map(mapRef.current!, {
        zoomControl: true,
        scrollWheelZoom: true,
        attributionControl: true,
      })
      mapInstance.current = map

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map)

      const withCoords = pins.filter(p => p.lat && p.lng)

      const c = { places: 0, services: 0, events: 0 }
      const created: { marker: any; pin: GlobalPin }[] = []

      withCoords.forEach(pin => {
        c[pin.collection]++
        const icon = L.divIcon({
          className: '',
          html: `<div style="
            width:22px;height:22px;
            border-radius:${pin.collection === 'services' ? '3px' : pin.collection === 'events' ? '50% 50% 50% 0' : '50% 50% 50% 0'};
            rotate:${pin.collection === 'services' ? '0deg' : '-45deg'};
            background:${pin.color};
            border:2.5px solid #fff;
            box-shadow:0 2px 8px rgba(0,0,0,.35)
          "></div>`,
          iconSize: [22, 22],
          iconAnchor: [11, pin.collection === 'services' ? 11 : 22],
          popupAnchor: [0, pin.collection === 'services' ? -14 : -26],
        })

        const clusterTag = pin.cluster
          ? `<div style="display:inline-flex;align-items:center;gap:5px;font-family:monospace;font-size:10px;letter-spacing:.1em;color:#4C6B50;margin-bottom:6px">
               <span style="width:5px;height:5px;border-radius:50%;background:#4C6B50;flex:none"></span>${pin.cluster}
             </div><br>`
          : ''

        const popup = `
          <div style="font-family:'Hanken Grotesk',sans-serif;min-width:160px">
            <div style="font-family:serif;font-size:15px;font-weight:400;color:#1E1A16;margin-bottom:5px;line-height:1.3">${pin.name}</div>
            ${clusterTag}
            <div style="display:inline-block;font-family:monospace;font-size:9px;letter-spacing:.1em;background:${pin.color}22;color:${pin.color};border:1px solid ${pin.color}44;padding:2px 7px;border-radius:2px;margin-bottom:8px">${pin.typeLabel.toUpperCase()}</div>
            <br>
            <a href="/${pin.collection}/${pin.slug}" style="font-size:12px;color:#2E3A21;text-decoration:none;border-bottom:1px solid #2E3A21">View details →</a>
          </div>`

        const marker = L.marker([pin.lat, pin.lng], { icon }).bindPopup(popup, { maxWidth: 220 })
        marker.addTo(map)
        created.push({ marker, pin })
      })

      allMarkers.current = created
      setCounts(c)

      if (withCoords.length > 1) {
        const group = L.featureGroup(created.map(x => x.marker))
        map.fitBounds(group.getBounds().pad(0.15))
      } else {
        map.setView([GORIS_LAT, GORIS_LNG], 12)
      }
    })

    return () => {
      if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null }
    }
  }, [])

  // Filter markers when filter changes
  useEffect(() => {
    allMarkers.current.forEach(({ marker, pin }) => {
      if (filter === 'all' || filter === pin.collection) {
        mapInstance.current?.hasLayer(marker) || marker.addTo(mapInstance.current)
      } else {
        mapInstance.current?.hasLayer(marker) && mapInstance.current.removeLayer(marker)
      }
    })
  }, [filter])

  const FILTERS: { key: Filter; label: string; count: number }[] = [
    { key: 'all',      label: 'All',      count: counts.places + counts.services + counts.events },
    { key: 'places',   label: 'Places',   count: counts.places },
    { key: 'services', label: 'Services', count: counts.services },
    { key: 'events',   label: 'Events',   count: counts.events },
  ]

  return (
    <div className="gmap-wrap">
      <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />

      {/* Filter bar */}
      {!hideCollectionFilter && (
        <div className="gmap-filters">
          {FILTERS.map(f => (
            <button
              key={f.key}
              type="button"
              className={`gmap-filter-btn${filter === f.key ? ' active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <span className="gmap-filter-count">{f.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Legend */}
      {legend ? (
        <div className="gmap-legend">
          <div className="gmap-legend-group">
            {legend.map(item => (
              <div className="gmap-legend-row" key={item.label}>
                <span className="gmap-square" style={{ background: item.color }} />
                {item.label}
              </div>
            ))}
          </div>
        </div>
      ) : !hideCollectionFilter && (
        <div className="gmap-legend">
          <div className="gmap-legend-group">
            <div className="gmap-legend-head">Places</div>
            <div className="gmap-legend-row"><span className="gmap-dot" style={{background:'#B5512F'}} />Historical Sites</div>
            <div className="gmap-legend-row"><span className="gmap-dot" style={{background:'#7B3A8F'}} />Museums</div>
            <div className="gmap-legend-row"><span className="gmap-dot" style={{background:'#D4A017'}} />Churches</div>
            <div className="gmap-legend-row"><span className="gmap-dot" style={{background:'#4C8B50'}} />Natural</div>
            <div className="gmap-legend-row"><span className="gmap-dot" style={{background:'#3A6FA8'}} />Landmarks</div>
          </div>
          <div className="gmap-legend-divider" />
          <div className="gmap-legend-group">
            <div className="gmap-legend-head">Services</div>
            <div className="gmap-legend-row"><span className="gmap-square" style={{background:'#3B4A78'}} />Hotels</div>
            <div className="gmap-legend-row"><span className="gmap-square" style={{background:'#B07A2B'}} />Restaurants</div>
            <div className="gmap-legend-row"><span className="gmap-square" style={{background:'#4C6B50'}} />Visitor Info</div>
            <div className="gmap-legend-row"><span className="gmap-square" style={{background:'#9A4A4A'}} />Other</div>
          </div>
          <div className="gmap-legend-divider" />
          <div className="gmap-legend-group">
            <div className="gmap-legend-head">Events</div>
            <div className="gmap-legend-row"><span className="gmap-dot" style={{background:'#7A4A9A'}} />Events</div>
          </div>
        </div>
      )}

      <div ref={mapRef} className="gmap-map" />
    </div>
  )
}
